import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    document.addEventListener('sst:bundle:first-selection-change', this.#handleBundleFirstSelectionChange, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  #bundleColorTimer = null;
  #fetchController = null;

  #handleBundleFirstSelectionChange = (event) => {
    const { color, productUrl } = event.detail || {};
    if (!color) return;

    // Debounce: wait until the user has finished interacting with the dropdown
    // before fetching, so the open <select> isn't destroyed mid-interaction.
    clearTimeout(this.#bundleColorTimer);
    this.#bundleColorTimer = setTimeout(() => {
      this.#fetchGalleryForColor(color, productUrl || null);
    }, 400);
  };

  async #fetchGalleryForColor(color, siblingProductUrl) {
    const section = this.closest('.shopify-section');
    const variantPicker = section?.querySelector('variant-picker[data-sst-variants]');
    if (!variantPicker) return;

    const sectionId = variantPicker.dataset.sectionId;
    if (!sectionId) return;

    let fetchUrl;

    if (siblingProductUrl) {
      // Combined Listings: sibling product URL provided directly by the bundle picker.
      // Fetch first available variant of that sibling product for the gallery render.
      const cleanUrl = siblingProductUrl.split('?')[0];
      try {
        const handle = cleanUrl.split('/products/')[1];
        if (handle) {
          const productData = await fetch(`/products/${handle}.js`).then((r) => r.json());
          const firstVariant = productData.variants?.find((v) => v.available) || productData.variants?.[0];
          if (firstVariant) {
            fetchUrl = `${cleanUrl}?section_id=${sectionId}&variant=${firstVariant.id}`;
          }
        }
      } catch {}
    }

    if (!fetchUrl) {
      // Fallback: derive from current product's variant data (old single-product structure).
      const productUrl = variantPicker.dataset.productUrl?.split('?')[0];
      if (!productUrl) return;

      let variants, optionNames;
      try {
        variants = JSON.parse(variantPicker.dataset.sstVariants || '[]');
        optionNames = JSON.parse(variantPicker.dataset.sstOptions || '[]');
      } catch {
        return;
      }

      const colorIdx = optionNames.findIndex((n) => /colou?r/i.test(n));
      if (colorIdx === -1) return;

      const match = variants.find((v) => v.options[colorIdx]?.toLowerCase() === color.toLowerCase());
      if (!match) return;

      fetchUrl = `${productUrl}?section_id=${sectionId}&variant=${match.id}`;
    }

    // Fetch ONLY the section HTML — do NOT use fetchUpdatedSection which morphs
    // the variant picker and resets pack selection.
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    try {
      const response = await fetch(fetchUrl, { signal: this.#fetchController.signal });
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const newGallery = html.querySelector('media-gallery');
      if (newGallery) {
        this.replaceWith(newGallery);
      }
    } catch (err) {
      if (err.name !== 'AbortError') console.error('[media-gallery]', err);
    }
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
